import React from 'react';
import { Link } from 'react-router-dom';
import LegalDocument from '../components/LegalDocument';
import { MarketingNav, MarketingFooter } from '../components/MarketingChrome';
import legal from '../lib/legal.generated';
import { hasReviewMarkers } from '../lib/legalMarkdown';
import { getHostInfo } from '../lib/hosts';
import { LegalHoldingPage, TermsPage as TermsHolding, PrivacyPage as PrivacyHolding } from './LegalHoldingPage';

/**
 * /terms and /privacy — the real pages, behind two gates.
 *
 * The documents live in the repository root (`PRIVACY-POLICY.md`, `TERMS-OF-SERVICE.md`)
 * and are copied in by `scripts/gen_legal_pages.mjs`. The real page shows ONLY when both
 * of these are true; otherwise the visitor sees the holding page from LegalHoldingPage.js:
 *
 *   1. The copied text carries no `>>> … <<<` review markers and no draft banner.
 *   2. LEGAL_PAGES_LIVE below is true.
 *
 * ⚠️ WHY TWO GATES. Anybody signing up is agreeing to whatever is at these URLs, and
 * Google's and Microsoft's reviewers read them. Publishing a draft privacy policy would be
 * worse than the holding page — see PAGES-NEEDED.md. So going live is a deliberate act,
 * done on purpose, per document: resolve every marker, delete the draft banner, run the
 * copy script, commit the .md and the generated file. (The constant is already true.)
 *
 * PREVIEW. On your own PC (bare localhost) and on staging (`holidayhive.dev`, which sits
 * behind a username and password — Rob's decision, 11/09/2026) a draft is shown with a red
 * bar and every marker highlighted, so the layout can be checked and the text read on a
 * phone or sent to the solicitor as a link. Production (`holidayhive.co.uk`) never takes
 * that branch: the draft text is in its bundle but the page will not show it.
 */
// Flipped to true on 14/09/2026 for the privacy policy (decision 13/09/2026: live on the
// drafter's recommendations, solicitor review afterwards — LAUNCH-CHECKLIST.md). The other
// six documents still carry markers, so gate 1 keeps each of them on its holding page until
// its own markers are cleared; nothing else needs to change here for those.
export const LEGAL_PAGES_LIVE = true;

/** Hosts allowed to show a draft preview. Never the production domain. */
const PREVIEW_HOST = /(^|\.)holidayhive\.dev$/;

/**
 * The page sits inside the marketing site's own top bar and footer (MarketingChrome), so
 * a reader can get back to pricing, the trial or the homepage — Rob, 11/09/2026: "when in
 * the new pages they need to be part of the site". Light-only, like the landing page.
 */
const Frame = ({ children }) => (
  <div className="min-h-screen overflow-hidden bg-hive-warm font-nunito text-base text-hive-navy">
    <MarketingNav />
    <main className="mx-auto w-full max-w-3xl px-4 pb-16 pt-4 sm:px-8">
      <div className="rounded-2xl border border-hive-line bg-white p-6 shadow-sm sm:p-10">
        {children}
        <p className="mt-10 border-t border-hive-line pt-6 text-hive-muted">
          <Link to="/register" className="text-hive-orange hover:underline">Back to sign up</Link>
        </p>
      </div>
    </main>
    <MarketingFooter />
  </div>
);

const LegalPage = ({ docKey, holding }) => {
  const doc = legal[docKey];
  if (!doc) return holding;
  const draft = hasReviewMarkers(doc.text);
  if (!draft && LEGAL_PAGES_LIVE) {
    return <Frame><LegalDocument text={doc.text} /></Frame>;
  }
  const { isBareLocal } = getHostInfo();
  if (isBareLocal || PREVIEW_HOST.test(window.location.hostname)) {
    return <Frame><LegalDocument text={doc.text} preview /></Frame>;
  }
  return holding;
};

export const TermsPage = () => <LegalPage docKey="terms" holding={<TermsHolding />} />;
export const PrivacyPage = () => <LegalPage docKey="privacy" holding={<PrivacyHolding />} />;

// The tier-1 pages from LEGAL-PAGES-PLAN.md (11/09/2026). Same two gates, same frame; the
// holding page is the generic one because nobody has signed up against these.
const holding = (title) => (
  <LegalHoldingPage title={title} blurb={`Our ${title.toLowerCase()} page is being prepared and isn't published yet.`} />
);
export const ContactPage = () => <LegalPage docKey="contact" holding={holding('Contact and company details')} />;
export const SubProcessorsPage = () => <LegalPage docKey="subprocessors" holding={holding('Sub-processors')} />;
export const CookiesPage = () => <LegalPage docKey="cookies" holding={holding('Cookies')} />;
export const AcceptableUsePage = () => <LegalPage docKey="acceptableUse" holding={holding('Acceptable use')} />;
export const TaxStatementPage = () => <LegalPage docKey="tax" holding={holding('Tax statement')} />;

export default LegalPage;
